/**
 * 失敗時のエフェクトコンポーネント
 * type: 'shake' | 'flash' | 'crack'
 *
 * 2026-06-12 R1: hex 直書きを mood トークン（var(--mg-*)）へ置換（色のみ・
 * 振動/フラッシュ計算・タイミング・API は不変）。濃淡は color-mix で作る。
 */
import { useEffect, useState, useRef } from 'react';

export function FailureEffect({ type = 'shake', duration = 500, onComplete }) {
  const [active, setActive] = useState(true);
  const [progress, setProgress] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const startTime = Date.now();
    let frameId;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const p = Math.min(elapsed / duration, 1);
      setProgress(p);

      if (p < 1) {
        frameId = requestAnimationFrame(animate);
      } else {
        setActive(false);
        onComplete?.();
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [duration, onComplete]);

  if (!active) return null;

  // 振動計算
  const shakeIntensity = type === 'shake' ? (1 - progress) * 10 : 0;
  const shakeX = Math.sin(progress * Math.PI * 20) * shakeIntensity;
  const shakeY = Math.cos(progress * Math.PI * 15) * shakeIntensity * 0.5;

  // フラッシュ計算
  const flashOpacity = type === 'flash'
    ? Math.sin(progress * Math.PI * 3) * (1 - progress) * 0.4
    : 0;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 9999,
        transform: `translate(${shakeX}px, ${shakeY}px)`
      }}
    >
      {/* 赤フラッシュ */}
      {(type === 'flash' || type === 'shake') && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'var(--mg-error)',
            opacity: type === 'flash' ? flashOpacity : (1 - progress) * 0.15,
            transition: 'none'
          }}
        />
      )}

      {/* 周辺ビネット */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle, transparent 40%, color-mix(in srgb, var(--mg-error) ${30 * (1 - progress)}%, transparent) 100%)`,
          transition: 'none'
        }}
      />

      {/* ✗マーク */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${1 + (1 - progress) * 0.3})`,
          fontSize: '3rem',
          fontWeight: 'bold',
          color: 'var(--mg-error)',
          textShadow: '0 2px 8px color-mix(in srgb, var(--mg-bg) 50%, transparent)',
          opacity: (1 - progress) * 0.8
        }}
      >
        ✗
      </div>

      {/* クラック効果 */}
      {type === 'crack' && (
        <svg
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '200px',
            height: '200px',
            opacity: (1 - progress) * 0.6
          }}
          viewBox="0 0 100 100"
        >
          <path
            d="M50 10 L45 35 L30 40 L40 50 L35 70 L50 60 L65 70 L60 50 L70 40 L55 35 Z"
            fill="none"
            // stroke は style 経由で渡す（var() は SVG の presentation 属性では解釈されないため）
            style={{ stroke: 'var(--mg-error)' }}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

export default FailureEffect;
